import IconHuman from 'assets/icons/IconHuman.svg'
import IconIndicatorDown from 'assets/icons/IconIndicatorDown.svg'
import Button from 'components/common/Button'
import * as Common from 'components/common/Common'
import * as Typography from 'components/common/Typography'
import { TFormSearchRoomChosenInput } from 'components/forms/FormSearchRoom'
import InputIncrementer from 'components/inputs/InputIncrementer'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  selectFitCustomResolution,
  selectIsKiosk,
  setKioskInputOpened,
} from 'redux/slices/app'
import { selectRoomSearchParams } from 'redux/slices/room'
import { IFindRoomForm, useOnClickOutside } from 'shared-ui'
import styled, { css } from 'styled-components'

interface IInputPersonCountProps {
  form: UseFormReturn<IFindRoomForm>
  selectedInput: TFormSearchRoomChosenInput
  setSelectedInput: Dispatch<SetStateAction<TFormSearchRoomChosenInput>>
}

const StyledDropdownModal = styled(Common.DropdownModal)<{
  shouldSqueeze?: boolean
}>(
  ({ shouldSqueeze }) => css`
    left: ${shouldSqueeze ? '0' : 'auto'};
    width: ${shouldSqueeze ? '100%' : 'auto'};
  `
)

const InputPersonCount = ({
  form,
  selectedInput,
  setSelectedInput,
}: IInputPersonCountProps) => {
  const { t } = useTranslation()
  const ref = useRef(null)
  const isMobile = useAppSelector(state =>
    selectFitCustomResolution(state, 1266)
  )
  const shouldSqueezePicker = useAppSelector(state =>
    selectFitCustomResolution(state, 440)
  )
  const isKiosk = useAppSelector(selectIsKiosk)
  const dispatch = useAppDispatch()
  const searchData = useAppSelector(selectRoomSearchParams)

  useEffect(() => {
    form.setValue('adults', searchData.adults)
    form.setValue('children', searchData.children)
    form.setValue('rooms', searchData.rooms)
  }, [searchData])

  const isOpen = selectedInput === 'rooms'

  useOnClickOutside(ref, () => {
    setSelectedInput(null)
    if (isKiosk) {
      dispatch(setKioskInputOpened(false))
    }
  })

  const handleIconClick = () => {
    if (isOpen) {
      setSelectedInput(null)
      if (isKiosk) {
        dispatch(setKioskInputOpened(false))
      }
    } else {
      setSelectedInput('rooms')
      if (isKiosk) {
        dispatch(setKioskInputOpened(true))
      }
    }
  }

  const handleDoneButton = () => {
    setSelectedInput(null)
    dispatch(setKioskInputOpened(false))
  }

  const { watch, formState } = form
  const { adults, children, rooms } = watch()

  const adultsError = formState?.errors.adults as { message: string }
  const roomsError = formState?.errors.rooms as { message: string }

  return (
    <Common.Div
      // ml={isMobile ? 0 : 2}
      mb={isMobile ? 2 : 0}
      flex="column"
      gap="8px"
    >
      <Common.DropdownWithIcon
        onClick={handleIconClick}
        isMobile={isMobile}
      >
        <Common.SvgIcon
          src={IconHuman}
          w="20px"
          h="20px"
        />
        <Typography.Body2
          secondary
          semiBold
          mr={1}
          style={{
            minWidth: '200px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Body2
            secondary
            semiBold
            style={{
              display: 'inline-block',
              minWidth: '2ch',
              textAlign: 'center',
            }}
          >
            {adults}
          </Typography.Body2>
          {` ${t('inputPerson.adults')} | `}
          <Typography.Body2
            secondary
            semiBold
            style={{
              display: 'inline-block',
              minWidth: '2ch',
              textAlign: 'center',
            }}
          >
            {children ?? '0'}
          </Typography.Body2>
          {` ${t('inputPerson.children')} | `}
          <Typography.Body2
            secondary
            semiBold
            style={{
              display: 'inline-block',
              minWidth: '2ch',
              textAlign: 'center',
            }}
          >
            {rooms}
          </Typography.Body2>
          {` ${t('inputPerson.rooms')}`}
        </Typography.Body2>
        <Common.SvgIcon
          src={IconIndicatorDown}
          w="17px"
          h="10px"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </Common.DropdownWithIcon>

      {isOpen && (
        <StyledDropdownModal
          ref={ref}
          shouldSqueeze={shouldSqueezePicker}
          w={isKiosk ? '296px' : 'auto'}
        >
          <Common.Div flex="column">
            <Common.Div
              flex="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography.Body1 secondary>
                {t('inputPerson.adults')}
              </Typography.Body1>
              <InputIncrementer
                name="adults"
                form={form}
                min={1}
              />
            </Common.Div>

            <Common.Div
              flex="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography.Body1
                secondary
                mr={2}
              >
                {t('inputPerson.children')}
              </Typography.Body1>
              <InputIncrementer
                name="children"
                form={form}
                min={0}
              />
            </Common.Div>
            <Common.Div
              flex="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography.Body1 secondary>
                {t('inputPerson.rooms')}
              </Typography.Body1>
              <InputIncrementer
                name="rooms"
                form={form}
                min={1}
              />
            </Common.Div>

            <Button onClick={handleDoneButton}>
              {t('inputPerson.button')}
            </Button>
          </Common.Div>
        </StyledDropdownModal>
      )}
      {adultsError && (
        <Typography.ErrorMessage>
          {adultsError?.message ?? ''}
        </Typography.ErrorMessage>
      )}
      {roomsError && (
        <Typography.ErrorMessage>
          {roomsError?.message ?? ''}
        </Typography.ErrorMessage>
      )}
    </Common.Div>
  )
}

export default InputPersonCount
